// The mock preview's sample data as the instances' logs (instance.ts serves them): every record of the fixture board
// (poc.ts) and the platform fixture (platform.ts: chat, members, the Connection) as one creation event, the home's
// records in the home's log, the connected digiterium's (address ids `digiterium/<id>`) in its own. What the board
// and chat show of them (blocked, open counts, unread, last message, columns) the client core derives.
import { instanceOf as instanceIn, type Message, type Snapshot, type Thread } from "../types";
import type { PlatformState } from "./platform";

export type MockChange = { field: string; from: unknown; to: unknown; note?: string; /** a many edge or links */ many?: boolean };
export type MockEvent = { seq: number; tx?: string; at: string; by: string; kind: string; id: string; space: string | null; fields: MockChange[] };
export type MockLog = { head: number; events: MockEvent[] };

const REMOTE = "digiterium";
const instanceOf = (id: string) => instanceIn(id) ?? "";
/** A ref as the record's instance writes it: a home id seen from digiterium is digiterium's same-named record. */
const refIn = (inst: string, id: string) => (inst && !id.includes("/") ? `${inst}/${id}` : id);

export function mockLogs(s: Snapshot, p: PlatformState, o: { me: string; body: (title: string) => string | null; context: [string, { id: string; note: string | null }[]][]; links: Record<string, { url: string; kind: string; title: string | null; addedAt: string }[]> }): Record<string, MockLog> {
	const logs: Record<string, MockLog> = { "": { head: 0, events: [] }, [REMOTE]: { head: 0, events: [] } };
	const emit = (kind: string, id: string, space: string | null, at: string, by: string, fields: Record<string, unknown>, many: string[] = [], notes: Record<string, string | null> = {}) => {
		const inst = instanceOf(id);
		const log = logs[inst]!;
		const changes: MockChange[] = [];
		for (const [field, v] of Object.entries(fields)) {
			if (v === null || v === undefined) continue;
			if (many.includes(field) || field === "links")
				for (const x of v as unknown[]) changes.push({ field, from: null, to: x, many: true, ...(typeof x === "string" && notes[x] ? { note: notes[x]! } : {}) });
			else changes.push({ field, from: null, to: v });
		}
		log.events.push({ seq: ++log.head, at, by: refIn(inst, by), kind, id, space, fields: changes });
	};
	const t0 = "2026-10-01T09:00:00.000Z";
	// persons in both instances (digiterium's are its own records of the same people)
	for (const inst of ["", REMOTE])
		for (const x of s.persons) emit("truss.Person", refIn(inst, x.id), null, t0, o.me, { name: x.name, type: x.kind });
	// a stub ancestor (above the person's spaces) carries its name and parent only
	for (const x of s.projects) emit("truss.Space", x.id, x.id, t0, o.me, x.stub ? { name: x.name, parent: x.parent } : { name: x.name, status: x.status, repos: x.repoIds, parent: x.parent, type: x.type }, ["repos"]);
	// every root space holds the board's default statuses (the engine's statuses controller writes them on a real
	// instance, features/tasks/controller; the mock has no engine): todo, doing, review, done, dropped
	const DEFAULTS: [string, string, string][] = [
		["todo", "unstarted", "#8a8a8a"],
		["doing", "started", "#4b6fa8"],
		["review", "started", "#a07a2c"],
		["done", "completed", "#4d7c5b"],
		["dropped", "canceled", "#b5b5b5"],
	];
	for (const x of s.projects.filter((p) => !p.parent))
		DEFAULTS.forEach(([name, category, color], i) => {
			const inst = instanceOf(x.id);
			const key = x.id.split("/").at(-1)!.replace(/^spc_/, "");
			emit("hq.tasks.Status", refIn(inst, `sts_${key}${name}`), x.id, t0, o.me, { name, category, color, order: `${i + 1}`, fixed: true });
		});
	for (const r of s.repos) emit("hq.tasks.Repo", r.id, s.projects.find((x) => x.repoIds.includes(r.id))?.id ?? null, t0, o.me, { path: r.path });
	const context = new Map(o.context);
	for (const t of s.tasks) {
		const inst = instanceOf(t.id);
		const ctx = context.get(t.id) ?? [];
		const links = [...(t.cover ? [{ url: t.cover, kind: "blob", title: t.cover.split("/").at(-1)!, addedAt: t.createdAt }] : []), ...(o.links[t.id] ?? [])];
		emit(
			"hq.tasks.Task",
			t.id,
			t.projectId,
			t.createdAt,
			o.me,
			{
				title: t.title,
				body: o.body(t.title),
				status: t.status,
				rank: t.rank,
				assignee: t.assigneeId && refIn(inst, t.assigneeId),
				parent: t.parentId,
				blockedBy: t.blockedBy.map((b) => refIn(inst, b)),
				context: ctx.map((c) => c.id),
				links,
			},
			["blockedBy", "context"],
			Object.fromEntries(ctx.map((c) => [c.id, c.note])),
		);
	}
	for (const m of p.members) if (m.space) emit("truss.Member", m.id, m.space.id, t0, o.me, { person: m.person.id, status: m.status });
	for (const c of p.connections.filter((x) => x.direction === "outgoing" && x.status === "active"))
		emit("truss.Connection", c.id, null, c.createdAt, o.me, { instance: c.instance, url: c.url, status: "active", person: o.me });
	const msgs = (t: Thread) => p.messages.filter((m) => m.threadId === t.id);
	for (const t of p.threads) {
		const first = msgs(t)[0]?.at ?? t0;
		emit("hq.chat.Thread", t.id, t.spaceId, first, o.me, { title: t.title, about: t.about, status: "open" });
		msgs(t).forEach((m: Message, i) => {
			const inst = instanceOf(t.id);
			emit("hq.chat.Message", m.id, t.spaceId, m.at ?? first, m.by.id, { thread: t.id, body: m.body, status: "posted" });
			// reactions: mine is the signed-in person's, the rest by the others in the fixture
			let other = 0;
			for (const r of m.reactions)
				for (let n = 0; n < r.count; n++) {
					const by = r.mine && n === 0 ? o.me : (s.persons.filter((x) => x.id !== o.me)[other++ % (s.persons.length - 1)]?.id ?? o.me);
					emit("hq.chat.Reaction", `${inst ? `${inst}/` : ""}rct_${m.id.split("/").at(-1)}${r.emoji.codePointAt(0)}${n}`, t.spaceId, m.at ?? first, by, { message: m.id, emoji: r.emoji, status: "active" });
				}
			void i;
		});
		// my read mark (private, in the home's log): the fixture's unread count of others' messages are after it
		const others = msgs(t).filter((m) => m.by.id !== o.me);
		const read = others.at(-1 - t.unread) ?? null;
		if (read?.at) emit("hq.chat.ReadMark", `rmk_${t.id.replace("/", "_")}`, null, read.at, o.me, { thread: t.id, upTo: read.at });
	}
	return logs;
}
